import { jsPDF } from 'jspdf';
import autoTable, { UserOptions, ThemeType } from 'jspdf-autotable';
import { ChecklistData } from '../types';
import IvicosStrings from 'kits/language/stringKit';

export const generatePDFReport = async (checklistData: ChecklistData): Promise<void> => {
    const doc = new jsPDF();
    const margin = 20;

    // Define consistent styles
    const primaryColor = [0, 102, 204] as [number, number, number];
    const secondaryColor = [248, 250, 252] as [number, number, number]; // Lighter background
    const textColor = [71, 85, 105] as [number, number, number];

    const tableStyle: Partial<UserOptions> = {
        theme: 'grid' as ThemeType,
        headStyles: {
            fillColor: [235, 245, 255], // Light blue header
            textColor: primaryColor,
            fontStyle: 'bold',
            halign: 'left',
            fontSize: 11,
            cellPadding: 10,
            minCellHeight: 16,
            valign: 'middle'
        },
        bodyStyles: {
            textColor: textColor,
            fontSize: 10,
            cellPadding: 10,
            minCellHeight: 16,
            valign: 'middle'
        },
        alternateRowStyles: {
            fillColor: secondaryColor
        },
        columnStyles: {
            0: {
                fontStyle: 'bold',
                cellWidth: 120, // Slightly wider for better readability
                fillColor: [250, 250, 250]
            },
            1: {
                cellWidth: 'auto'
            }
        },
        didDrawPage: function (data) {
            // Add page number at the bottom of each page
            const pageNumber = (doc as any).internal.pages.length;
            const pageCount = (doc as any).internal.pages.length;
            const str = `${IvicosStrings.webRTCDiagnosticsGeneratedReport.page} ${pageNumber} ${IvicosStrings.webRTCDiagnosticsGeneratedReport.of} ${pageCount}`;
            doc.setFontSize(8);
            doc.setTextColor(128);
            doc.text(str, doc.internal.pageSize.width - margin - doc.getTextWidth(str), doc.internal.pageSize.height - 10);
        },
        margin: { top: margin, right: margin, bottom: margin + 15, left: margin }, // Add bottom margin for page numbers
        tableWidth: 'auto',
        styles: {
            font: 'helvetica',
            lineColor: [226, 232, 240],
            lineWidth: 0.1
        },
        didParseCell: function (data) {
            if (data.section === 'body' && data.column.index === 1) {
                const value = data.cell.text[0];
                if (value === IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed) {
                    data.cell.styles.textColor = [22, 163, 74]; // Green
                } else if (value === IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked) {
                    data.cell.styles.textColor = [220, 38, 38]; // Red
                } else if (value === IvicosStrings.yesAction) {
                    data.cell.styles.textColor = [22, 163, 74]; // Green
                } else if (value === IvicosStrings.noAction) {
                    data.cell.styles.textColor = [220, 38, 38]; // Red
                }
            }
        }
    };

    // Add header
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, doc.internal.pageSize.width, 30, 'F');

    // Add title
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(IvicosStrings.webRTCDiagnosticsGeneratedReport.title, margin, 15);

    // Add timestamp
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`${IvicosStrings.webRTCDiagnosticsGeneratedReport.generatedOn} ${new Date().toLocaleString()}`, margin, 25);

    // Reset text color and set Y position after header
    let yPos = 45;
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);

    // Add sections with consistent spacing
    const addSection = (title: string, data: string[][], headers: string[]) => {
        // Check if we need a new page
        if (yPos > doc.internal.pageSize.height - 150) {
            doc.addPage();
            yPos = margin;
        }

        // Add spacing before section
        yPos += 10;

        // Section header with underline
        doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setLineWidth(0.5);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(title, margin, yPos);

        // Draw underline
        const titleWidth = doc.getTextWidth(title);
        doc.line(margin, yPos + 1, margin + titleWidth, yPos + 1);

        autoTable(doc, {
            ...tableStyle,
            startY: yPos + 15, // More space after section title
            head: [headers],
            body: data
        });

        yPos = (doc as any).lastAutoTable.finalY + 20; // More space between sections
    };

    // Basic Information
    addSection(
        IvicosStrings.webRTCDiagnosticsGeneratedReport.basicInformation,
        [
            [IvicosStrings.webRTCDiagnosticsGeneratedReport.browser, checklistData.browser],
            [IvicosStrings.webRTCDiagnosticsGeneratedReport.browserUpdated, checklistData.browserUpdated],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.browserVersion,
                checklistData.browserVersion || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.internetConnection,
                checklistData.internetConnection || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.speedTestResult,
                checklistData.speedTestResult || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.operatingSystem,
                checklistData.operatingSystem || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ]
        ],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.category, IvicosStrings.webRTCDiagnosticsGeneratedReport.status]
    );

    // Device Checks
    addSection(
        IvicosStrings.webRTCDiagnosticsGeneratedReport.deviceChecks,
        [
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.microphoneWorking,
                checklistData.microphoneWorking || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.cameraWorking,
                checklistData.cameraWorking || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.permissionsGranted,
                checklistData.permissionsGranted || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
            ]
        ],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.check, IvicosStrings.webRTCDiagnosticsGeneratedReport.status]
    );

    // Call Issues
    const callIssues = [
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.callIssues, checklistData.callIssues || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided],
        [
            IvicosStrings.webRTCDiagnosticsGeneratedReport.othersCanSeeHear,
            checklistData.othersCanSeeHear || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided
        ],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.usingVPN, checklistData.usingVPN || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.managedIT, checklistData.managedIT || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.regionalIT, checklistData.regionalIT || IvicosStrings.webRTCDiagnosticsGeneratedReport.notProvided]
    ];
    if (checklistData.choppyDescription) {
        callIssues.push([IvicosStrings.webRTCDiagnosticsGeneratedReport.choppyDescription, checklistData.choppyDescription]);
    }
    addSection(IvicosStrings.webRTCDiagnosticsGeneratedReport.callIssues, callIssues, [
        IvicosStrings.webRTCDiagnosticsGeneratedReport.issue,
        IvicosStrings.webRTCDiagnosticsGeneratedReport.status
    ]);

    // Firewall Configuration
    addSection(
        IvicosStrings.webRTCDiagnosticsGeneratedReport.firewallConfiguration,
        [
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.turnServer,
                checklistData.firewallConfig.turnServer
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.jvbServer,
                checklistData.firewallConfig.jvbServer
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.jitsiServer,
                checklistData.firewallConfig.jitsiServer
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.port80,
                checklistData.firewallConfig.port80
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.port443,
                checklistData.firewallConfig.port443
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.port4443,
                checklistData.firewallConfig.port4443
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ],
            [
                IvicosStrings.webRTCDiagnosticsGeneratedReport.port10000,
                checklistData.firewallConfig.port10000
                    ? IvicosStrings.webRTCDiagnosticsGeneratedReport.allowed
                    : IvicosStrings.webRTCDiagnosticsGeneratedReport.blocked
            ]
        ],
        [IvicosStrings.webRTCDiagnosticsGeneratedReport.servicePort, IvicosStrings.webRTCDiagnosticsGeneratedReport.status]
    );

    // Additional Information
    if (checklistData.issueStartDate || checklistData.issueFrequency || checklistData.otherParticipantsIssue || checklistData.additionalDetails) {
        const additionalInfo: [string, string][] = [];
        if (checklistData.issueStartDate) additionalInfo.push([IvicosStrings.webRTCDiagnosticsGeneratedReport.issueStartDate, checklistData.issueStartDate]);
        if (checklistData.issueFrequency) additionalInfo.push([IvicosStrings.webRTCDiagnosticsGeneratedReport.issueFrequency, checklistData.issueFrequency]);
        if (checklistData.otherParticipantsIssue)
            additionalInfo.push([IvicosStrings.webRTCDiagnosticsGeneratedReport.otherParticipantsIssue, checklistData.otherParticipantsIssue]);
        if (checklistData.additionalDetails)
            additionalInfo.push([IvicosStrings.webRTCDiagnosticsGeneratedReport.additionalDetails, checklistData.additionalDetails]);

        addSection(IvicosStrings.webRTCDiagnosticsGeneratedReport.additionalInformation, additionalInfo, [
            IvicosStrings.webRTCDiagnosticsGeneratedReport.category,
            IvicosStrings.webRTCDiagnosticsGeneratedReport.details
        ]);
    }

    // Add screenshots if available
    if (checklistData.screenshots.length > 0) {
        // Start screenshots on a new page
        doc.addPage();
        yPos = margin;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(IvicosStrings.webRTCDiagnosticsGeneratedReport.screenshots, margin, yPos);
        yPos += 10;

        for (let i = 0; i < checklistData.screenshots.length; i++) {
            const screenshot = checklistData.screenshots[i];
            try {
                // Read the screenshot file
                const reader = new FileReader();
                await new Promise((resolve) => {
                    reader.onload = resolve;
                    reader.readAsDataURL(screenshot);
                });

                const imgData = reader.result as string;
                const imageData = imgData.split(',')[1];

                // Calculate dimensions
                const pageWidth = doc.internal.pageSize.width;
                const pageHeight = doc.internal.pageSize.height;
                const usableWidth = pageWidth - 2 * margin;
                const usableHeight = pageHeight - 2 * margin;

                // Get image dimensions
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = resolve;
                    img.src = imgData;
                });

                // Calculate scaling
                const imgRatio = img.width / img.height;
                const pageRatio = usableWidth / usableHeight;
                let finalWidth = usableWidth;
                let finalHeight = usableHeight;

                if (imgRatio > pageRatio) {
                    finalWidth = usableWidth;
                    finalHeight = usableWidth / imgRatio;
                } else {
                    finalHeight = usableHeight;
                    finalWidth = usableHeight * imgRatio;
                }

                // Center the image
                const xOffset = margin + (usableWidth - finalWidth) / 2;

                // Add screenshot label
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text(`Screenshot ${i + 1}`, margin, yPos);

                // Add the image with a border
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.rect(xOffset - 1, yPos + 4, finalWidth + 2, finalHeight + 2);

                doc.addImage(imageData, 'JPEG', xOffset, yPos + 5, finalWidth, finalHeight, `screenshot-${i}`, 'MEDIUM');

                if (i < checklistData.screenshots.length - 1) {
                    doc.addPage();
                    yPos = margin;
                }
            } catch (error) {
                console.error('Error adding screenshot:', error);
                doc.setTextColor(220, 38, 38);
                doc.text(`Error loading screenshot ${i + 1}`, margin, yPos + 20);
            }
        }
    }

    // Save the PDF
    doc.save('ivCAMPUS-Troubleshooting-Report.pdf');
};
